import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart as RechartsRadarChart,
  ResponsiveContainer,
  Tooltip
} from "recharts";
import { sysColors } from "@/assets/theme/colors";
import classNames from "classnames";
import type { SVGProps } from "react";

import ChartSkeleton from "@/components/Skeleton/ChartSkeleton";
import ChartLegend from "./ChartLegend";
import ChartTooltip from "./ChartTooltip";

export type RadarChartSeries = {
  dataKey: string;
  name: string;
  color: string;
  fillOpacity?: number;
  formatTooltipValue?: (value: unknown, payload: Record<string, unknown>) => string | number;
};

export type RadarChartProps = {
  data: Record<string, string | number | boolean>[];
  series: RadarChartSeries[];
  angleKey?: string;
  maxValue?: number;
  title?: string;
  subtitle?: string;
  loading?: boolean;
  className?: string;
  height?: number;
  /**
   * When false, removes the card chrome (`border`, `rounded-lg`, and `p-6`) so the chart can sit
   * inside a parent card that owns padding and border. Default keeps the self-contained card look.
   */
  bordered?: boolean;
};

type AngleTickProps = SVGProps<SVGTextElement> & {
  payload?: { value?: string | number };
  x?: number;
  y?: number;
  cx?: number;
  cy?: number;
  textAnchor?: string;
};

type RadiusTickProps = SVGProps<SVGTextElement> & {
  payload?: { value?: string | number };
  x?: number;
  y?: number;
};

/** Split long axis labels onto two lines so side ticks don't clip the chart edge. */
function wrapAngleLabel(label: string): string[] {
  const words = label.trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1 || label.length <= 14) {
    return [label];
  }

  const midpoint = Math.ceil(words.length / 2);
  return [words.slice(0, midpoint).join(" "), words.slice(midpoint).join(" ")];
}

function AngleTick({ payload, x = 0, y = 0, cx = 0, cy = 0, textAnchor = "middle" }: AngleTickProps) {
  const label = String(payload?.value ?? "");
  const lines = wrapAngleLabel(label);
  // Nudge labels outward so the top category clears the scale ticks.
  const dx = x - cx;
  const dy = y - cy;
  const distance = Math.hypot(dx, dy) || 1;
  const outward = 20;
  const tickX = x + (dx / distance) * outward;
  const tickY = y + (dy / distance) * outward;
  const lineHeight = 14;
  const startDy = lines.length > 1 ? -((lines.length - 1) * lineHeight) / 2 : 0;

  return (
    <text
      x={tickX}
      y={tickY}
      textAnchor={textAnchor}
      dominantBaseline="middle"
      fill={sysColors["sys-brand-on-surface-variant"]}
      fontSize={12}
      fontWeight={600}
    >
      {lines.map((line, index) => (
        <tspan key={`${line}-${index}`} x={tickX} dy={index === 0 ? startDy : lineHeight}>
          {line}
        </tspan>
      ))}
    </text>
  );
}

function RadiusTick({ payload, x = 0, y = 0 }: RadiusTickProps) {
  const value = payload?.value;
  if (value === 0 || value === "0") {
    return null;
  }

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      dominantBaseline="middle"
      fill={sysColors["sys-brand-on-surface-variant"]}
      fontSize={11}
      fontWeight={600}
    >
      {String(value)}
    </text>
  );
}

const RadarChartComponent = ({
  data,
  series,
  angleKey = "name",
  maxValue = 5,
  title,
  subtitle,
  loading = false,
  className,
  height = 360,
  bordered = true
}: RadarChartProps) => {
  return (
    <div
      className={classNames(
        "flex flex-col items-start w-full h-full",
        bordered && "border border-sys-brand-outline rounded-lg p-6",
        className
      )}
      data-testid="radar-chart"
    >
      {title || subtitle ? (
        <div className="w-full mb-6">
          {title ? <p className="text-title-medium text-sys-brand-on-surface">{title}</p> : null}
          {subtitle ? <p className="text-body-medium text-sys-brand-on-surface-variant">{subtitle}</p> : null}
        </div>
      ) : null}

      <div className="w-full overflow-visible">
        {loading ? (
          <ChartSkeleton type="radar" />
        ) : (
          <>
            <ResponsiveContainer width="100%" height={height}>
              <RechartsRadarChart
                data={data}
                cx="50%"
                cy="50%"
                outerRadius="58%"
                margin={{ top: 40, right: 72, bottom: 28, left: 72 }}
              >
                <PolarGrid stroke="#E1E3E4" />
                <PolarAngleAxis dataKey={angleKey} tick={<AngleTick />} />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, maxValue]}
                  tickCount={maxValue + 1}
                  axisLine={false}
                  tick={<RadiusTick />}
                />
                <Tooltip
                  content={({ active = false, payload }) => {
                    const items =
                      payload?.map((entry: any) => {
                        const seriesItem = series.find((item) => item.dataKey === entry.dataKey);
                        const rawValue = entry.value;
                        const rowPayload = (entry.payload ?? {}) as Record<string, unknown>;
                        const value = seriesItem?.formatTooltipValue
                          ? seriesItem.formatTooltipValue(rawValue, rowPayload)
                          : rawValue;

                        return {
                          name: entry.name,
                          value,
                          color: entry.color ?? "#0061A4"
                        };
                      }) || [];
                    const tooltipSubtitle = payload?.[0]?.payload?.[angleKey];

                    return (
                      <ChartTooltip
                        active={active}
                        title={title}
                        subtitle={typeof tooltipSubtitle === "string" ? tooltipSubtitle : undefined}
                        items={items}
                      />
                    );
                  }}
                />
                {series.map((item) => (
                  <Radar
                    key={item.dataKey}
                    name={item.name}
                    dataKey={item.dataKey}
                    stroke={item.color}
                    fill={item.color}
                    fillOpacity={item.fillOpacity ?? 0.2}
                  />
                ))}
              </RechartsRadarChart>
            </ResponsiveContainer>

            <ChartLegend
              items={series.map((item) => ({
                name: item.name,
                color: item.color,
                visible: true
              }))}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default RadarChartComponent;
